import type { PlanKey, WorkflowTrigger } from "@sagot/constants";
import { setExit } from "./definition-model";
import type { ExitKey } from "./exit-slots";
import { conditionProblems, actionProblems, facingProblems, structureProblems, timeProblems } from "./graph-rules";
import { connectorProblems } from "./graph-rules-connector";
import { contentProblems } from "./graph-rules-content";
import { conversationProblems } from "./graph-rules-conversation";
import { serviceProblems } from "./graph-rules-service";
import { triggerProblems } from "./graph-rules-triggers";
import { p4Problems, planProblems } from "./graph-rules-p4";
import { edgesWithoutWaiting, reaches } from "./graph-loops";
import type { Problem } from "./problem-model";
import type { Definition } from "./types";

/**
 * Client-side mirror of the backend graph rules (`app/services/workflows/graph.py` and `plan_rules.py`), so the builder
 * can refuse a connection, and flag a problem on its tile, before the server would refuse the save. Only the rules that
 * need no database are here; `contracts/workflow-graph-cases.json` holds both sides to the same answers.
 */

export { edgesWithoutWaiting } from "./graph-loops";

/**
 * Whether pointing an exit at `target` would make its path lead back to itself without ever waiting for the visitor,
 * which the server refuses ("Only a path that waits for the visitor can loop").
 */
export function wouldLoopWithoutWait(definition: Definition, at: { path: string; index: number }, key: ExitKey, target: string): boolean {
  const edges = edgesWithoutWaiting(setExit(definition, at, key, target));
  return (edges.get(at.path)?.has(target) ?? false) && reaches(edges, target, at.path);
}

/**
 * Every problem the server would report for this definition without a database: structure, the visitor, actions, time,
 * conditions, reusable workflows and translations, and (given the workspace's `plan`) what the plan does not include,
 * which the server only checks when the workflow is published. Without a plan the plan rules are left out.
 */
export function checkGraph(definition: Definition, trigger: WorkflowTrigger, plan?: PlanKey): Problem[] {
  return [
    ...structureProblems(definition),
    ...actionProblems(definition, trigger),
    ...facingProblems(definition, trigger),
    ...timeProblems(definition, trigger),
    ...conditionProblems(definition),
    ...p4Problems(definition, trigger),
    ...connectorProblems(definition),
    ...conversationProblems(definition, trigger),
    ...triggerProblems(definition, trigger),
    ...contentProblems(definition, trigger),
    ...serviceProblems(definition, trigger),
    ...(plan ? planProblems(definition, trigger, plan) : []),
  ];
}
